import React, { useEffect } from 'react';
import './PrintMode.css';

const PrintMode = ({ template, onBackToCover, getNewTemplate }) => {
    
    const generatePrintVersion = () => {
        try {
            // Get a fresh template each time
            const currentTemplate = getNewTemplate();
            
            const printWindow = window.open('', '_blank');
            
            // Check if window was successfully opened
            if (!printWindow) {
                // Show a more helpful message with instructions
                const message = `🚫 Pop-up Blocked!\n\nYour browser blocked the print window. Here's how to fix it:\n\n1. Look for a pop-up blocked icon in your address bar\n2. Click it and select "Always allow pop-ups"\n3. Refresh the page and try again\n\nOR use the "Download HTML File" button below as an alternative!`;
                alert(message);
                return;
            }
            
            // Additional check to ensure document is available
            if (!printWindow.document) {
                alert('❌ Unable to access print window. Please try the download option below instead.');
                printWindow.close();
                return;
            }
        
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Mad Fills - Print Version</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    
                    body { 
                        font-family: 'Courier New', monospace; 
                        padding: 0;
                        margin: 0;
                        background: white;
                        font-size: 14px;
                        line-height: 1.4;
                        width: 100%;
                    }
                    
                    .page {
                        width: 100%;
                        margin: 0 auto;
                        padding: 1in 1.5in;
                        page-break-after: always;
                        box-sizing: border-box;
                        height: 10in;
                        max-height: 10in;
                        overflow: hidden;
                        display: block;
                        text-align: center;
                    }
                    
                    .page:last-child {
                        page-break-after: auto;
                        margin-bottom: 0;
                    }
                    
                    h1 { 
                        text-align: center; 
                        font-family: Impact, sans-serif; 
                        font-size: 2.4rem;
                        margin: 0 auto 1.2rem auto;
                        text-transform: uppercase;
                        letter-spacing: 3px;
                        border-bottom: 4px solid #333;
                        padding-bottom: 0.6rem;
                        max-width: 5.5in;
                    }
                    
                    h2 { 
                        text-align: center; 
                        font-family: Impact, sans-serif; 
                        font-size: 1.6rem;
                        margin: 1.2rem auto 1rem auto;
                        text-transform: uppercase;
                        letter-spacing: 2px;
                        max-width: 5.5in;
                    }
                    
                    .word-list { 
                        margin: 1.8rem auto; 
                        max-width: 5in;
                        text-align: left;
                    }
                    
                    .word-item { 
                        margin: 1rem 0; 
                        display: flex; 
                        align-items: center;
                        gap: 1.2rem; 
                        font-size: 1.3rem;
                    }
                    
                    .word-number {
                        font-weight: bold;
                        min-width: 32px;
                        font-size: 1.4rem;
                    }
                    
                    .word-prompt {
                        flex: 1;
                        font-weight: 500;
                    }
                    
                    .story { 
                        font-size: 1.3rem; 
                        line-height: 2.4; 
                        margin: 1.8rem auto; 
                        text-align: left;
                        max-width: 5.5in;
                    }
                    
                    .blank { 
                        border-bottom: 2px solid black; 
                        display: inline-block; 
                        min-width: 110px; 
                        height: 20px;
                        margin: 0 5px;
                        position: relative;
                    }
                    
                    .blank-number {
                        position: absolute;
                        top: -18px;
                        left: 50%;
                        transform: translateX(-50%);
                        font-size: 0.9rem;
                        font-weight: bold;
                        color: rgba(0, 0, 0, 0.15);
                    }
                    
                    .instructions {
                        text-align: center;
                        font-style: italic;
                        margin-bottom: 1.8rem;
                        font-size: 1.1rem;
                        color: #555;
                        max-width: 5in;
                        margin-left: auto;
                        margin-right: auto;
                    }
                    
                    .page-number {
                        text-align: center;
                        margin-top: 2rem;
                        font-size: 1.1rem;
                        font-weight: bold;
                    }
                    
                    .print-button-container {
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        z-index: 1000;
                        background: white;
                        padding: 10px;
                        border: 3px solid #333;
                        box-shadow: 4px 4px 0px #333;
                    }
                    
                    .print-now-button {
                        background: linear-gradient(135deg, #FFE66D 0%, #FF6B9D 100%);
                        color: #333;
                        border: 3px solid #333;
                        padding: 12px 20px;
                        font-size: 1rem;
                        font-weight: bold;
                        cursor: pointer;
                        text-transform: uppercase;
                        font-family: Impact, sans-serif;
                        letter-spacing: 1px;
                        box-shadow: 2px 2px 0px #333;
                    }
                    
                    .print-now-button:hover {
                        transform: translate(-1px, -1px);
                        box-shadow: 3px 3px 0px #333;
                    }
                    
                    .print-now-button:active {
                        transform: translate(1px, 1px);
                        box-shadow: 1px 1px 0px #333;
                    }
                    
                    @media print {
                        @page {
                            size: 8.5in 11in portrait;
                            margin: 0.5in;
                        }
                        
                        body { 
                            margin: 0; 
                            padding: 0;
                            font-size: 12px;
                        }
                        
                        .page { 
                            margin: 0; 
                            padding: 0.75in 1.2in;
                            page-break-after: always;
                            width: 100%;
                            height: 10in;
                            max-height: 10in;
                            overflow: hidden;
                            text-align: center;
                            display: block;
                            box-sizing: border-box;
                        }
                        
                        .page:last-child {
                            page-break-after: auto;
                        }
                        
                        h1 { font-size: 2.2rem; margin-bottom: 1rem; }
                        h2 { font-size: 1.5rem; margin: 1rem auto 0.8rem auto; }
                        .word-item { margin: 0.9rem 0; font-size: 1.2rem; }
                        .story { font-size: 1.2rem; line-height: 2.3; }
                        .instructions { font-size: 1rem; margin-bottom: 1.5rem; }
                        .page-number { margin-top: 1.5rem; font-size: 1rem; }
                        
                        .print-button-container { display: none; }
                    }
                    }
                </style>
            </head>
            <body>
                <!-- Print Button (hidden during print) -->
                <div class="print-button-container">
                    <button class="print-now-button" onclick="window.print()">
                        🖨️ PRINT NOW
                    </button>
                </div>
                
                <!-- Page 1: Word List -->
                <div class="page">
                    <h1>MAD FILLS</h1>
                    <p class="instructions">Fill in the blanks below, then turn to page 2 to read your story!</p>
                    
                    <div class="word-list">
                        ${currentTemplate.wordList.map(item => `
                            <div class="word-item">
                                <span class="word-number">${item.number}.</span>
                                <span class="word-prompt">${item.prompt}:</span>
                                <span class="blank"></span>
                            </div>
                        `).join('')}
                    </div>
                    
                    <div class="page-number">Page 1</div>
                </div>
                
                <!-- Page 2: Story -->
                <div class="page">
                    <h1>MAD FILLS</h1>
                    <h2>Your Hilarious Story!</h2>
                    
                    <div class="story">
                        ${currentTemplate.story.replace(/\{(\d+)\}/g, '<span class="blank"><span class="blank-number">$1</span></span>')}
                    </div>
                    
                    <div class="page-number">Page 2</div>
                </div>
            </body>
            </html>
        `);
        
        printWindow.document.close();
        } catch (error) {
            console.error('Error generating print version:', error);
            alert('❌ Error creating print version. Please try again or contact support if the problem persists.');
        }
    };

    return (
        <div className="print-mode-container">
            <div className="print-mode-content">
                <h1 className="booklet-title">PRINT MODE</h1>
                <div className="print-instructions">
                    <p>Print one for the kids!</p>
                    <p>Click the box below to open your printable 2-page Mud Lab.</p>
                    <ul>
                        <li><strong>Page 1:</strong> Word list for filling in</li>
                        <li><strong>Page 2:</strong> The story with numbered blanks</li>
                    </ul>
                </div>
                
                <div className="print-click-area" onClick={generatePrintVersion}>
                    <div className="print-box">
                        <h3>📄 CLICK HERE TO PRINT</h3>
                        <p>Your Mud Lab Booklet</p>
                        <div className="print-preview">
                            <div className="preview-page">📝📖 2-Page Mud Lab Booklet</div>
                        </div>
                    </div>
                </div>
                
                <div className="print-actions">
                    <button onClick={onBackToCover} className="back-button booklet-title">
                        ← BACK TO HOME
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PrintMode;